import { useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  CalendarCheck, Users, Star, Sparkles, ClipboardCheck,
  Sun, Gift, Gem, ArrowUpRight, Stethoscope, Smile, ShieldCheck, HeartPulse,
} from "lucide-react";
import { Reveal, MaskLine } from "../components/Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1489278353717-f64c6ee8a4d2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxzbWlsaW5nJTIwcGVyc29uJTIwd2FybSUyMGxpZ2h0fGVufDB8fHx8MTc4NDY5NzI0Nnww&ixlib=rb-4.1.0&q=85";
const CLINIC_IMG = "/palmisano-family.webp";
const TOOTHBRUSH_IMG =
  "https://images.pexels.com/photos/33215341/pexels-photo-33215341.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const WHITENING_IMG =
  "https://images.unsplash.com/photo-1564420228450-d9a5bc8d6565?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxkZW50YWwlMjB3aGl0ZW5pbmclMjBraXQlMjBhZXN0aGV0aWN8ZW58MHx8fHwxNzg0Njk3MjQ3fDA&ixlib=rb-4.1.0&q=85";

const earnActions = [
  { icon: CalendarCheck, label: "Show up to appointments", pts: 50 },
  { icon: Users, label: "Refer a friend", pts: 100 },
  { icon: Star, label: "Leave a Google review", pts: 75 },
  { icon: Sparkles, label: "Keep six-month cleanings", pts: 150 },
  { icon: ClipboardCheck, label: "Complete treatment plans", pts: 200 },
];

const rewards = [
  { icon: Sun, name: "Free Professional Whitening", cost: 500, img: WHITENING_IMG, frame: "arch" },
  { icon: Sparkles, name: "Premium Electric Toothbrush", cost: 400, img: TOOTHBRUSH_IMG, frame: "circle" },
  { icon: Gift, name: "$25 & $50 Gift Cards", cost: 300, img: null, frame: "circle" },
  { icon: Gem, name: "20% Off Cosmetic Treatment", cost: 700, img: null, frame: "arch" },
];

const chapters = [
  { n: "01", title: "Unhurried by design", body: "We keep our schedule calm so every visit feels personal — no rushing, no pressure, just honest guidance about your care." },
  { n: "02", title: "A family, for families", body: "The Palmisanos treat patients like neighbors, from a child's very first checkup to a grandparent's brand-new smile." },
  { n: "03", title: "Modern, gentle dentistry", body: "From digital x-rays to comfortable, up-to-date techniques, we pair a warm touch with precise technology so your care is easy and thorough." },
];

const services = [
  { icon: Smile, title: "General & Family", body: "Cleanings, checkups and honest guidance for every age." },
  { icon: Sparkles, title: "Cosmetic", body: "Whitening, veneers and refinements that look like you." },
  { icon: ShieldCheck, title: "Preventive", body: "Six-month hygiene that keeps problems small — or gone." },
  { icon: HeartPulse, title: "Restorative", body: "Crowns, implants and treatment plans done with care." },
];

export default function Home() {
  const location = useLocation();
  const heroRef = useRef(null);

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
    }
  }, [location]);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <main className="relative z-10">
      {/* HERO */}
      <section ref={heroRef} className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
              className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C05C42] mb-6"
            >
              Palmisano Family Dentistry · River Ridge, LA
            </motion.p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-[#2A2E26]">
              <MaskLine delay={0.15}>Gentle care</MaskLine>
              <MaskLine delay={0.3} className="italic text-[#C05C42]">for every</MaskLine>
              <MaskLine delay={0.45}>family smile.</MaskLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}
              className="mt-8 text-base sm:text-lg text-[#4A4E46] max-w-lg leading-relaxed"
            >
              A warm, family-owned practice offering complete dental care — from
              routine cleanings to cosmetic and restorative dentistry — for every
              age, in one calm and welcoming place.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.7 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <a href="tel:5047330871" data-testid="hero-call-btn"
                className="btn-terra font-semibold px-8 py-4 rounded-full inline-flex items-center gap-2">
                Call us · 504-733-0871
              </a>
              <a href="#services" data-testid="hero-explore-btn"
                className="font-semibold px-8 py-4 rounded-full border border-[#2A2E26]/20 text-[#2A2E26] hover:bg-[#2A2E26] hover:text-[#F9F6F0] transition-colors duration-300">
                Our services
              </a>
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="relative arch overflow-hidden aspect-[3/4] bg-[#E3DCCF]"
            >
              <motion.img
                src={HERO_IMG} alt="Smiling patient" style={{ y: imgY, scale: imgScale }}
                className="w-full h-[115%] object-cover"
              />
              <div className="absolute bottom-5 left-5 right-5 backdrop-blur-md bg-[#F9F6F0]/80 rounded-2xl px-5 py-4 flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-[#4A4E46]">Now welcoming</p>
                  <p className="font-display text-2xl text-[#2A2E26]">New patients</p>
                </div>
                <Smile className="text-[#C05C42]" size={28} />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="relative z-10 border-y border-[#2A2E26]/10 bg-[#A8B5A2]/40 py-5">
        <Marquee speed={40} gradient={false}>
          {Array.from({ length: 6 }).map((_, i) => (
            <span key={i} className="font-display text-2xl sm:text-3xl text-[#2A2E26] mx-8 flex items-center gap-8">
              Family Dentistry <span className="text-[#C05C42]">✦</span> Gentle Care
              <span className="text-[#C05C42]">✦</span> Healthy Smiles <span className="text-[#C05C42]">✦</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* MANIFESTO */}
      <section id="philosophy" className="scroll-mt-24 max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C05C42] mb-14">Our philosophy</p>
        <div className="space-y-16">
          {chapters.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.05}>
              <div className="grid md:grid-cols-12 gap-6 md:gap-8 items-start border-t border-[#2A2E26]/10 pt-10">
                <div className="md:col-span-3">
                  <span className="font-display text-7xl lg:text-8xl font-light text-[#C05C42]/30">{c.n}</span>
                </div>
                <h3 className="md:col-span-4 font-display text-3xl tracking-tight text-[#2A2E26]">{c.title}</h3>
                <p className="md:col-span-5 text-lg text-[#4A4E46] leading-relaxed">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="bg-[#2A2E26] text-[#F9F6F0] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-16 max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#A8B5A2] mb-4">Services</p>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight leading-tight">
              Complete care, <span className="italic text-[#C05C42]">under one roof</span>.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#F9F6F0]/10 rounded-3xl overflow-hidden">
            {services.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div data-testid={`service-card-${i}`} className="h-full bg-[#2A2E26] p-8 hover:bg-[#33382e] transition-colors duration-300">
                  <s.icon size={30} className="text-[#C05C42] mb-6" strokeWidth={1.4} />
                  <h3 className="font-display text-2xl mb-3">{s.title}</h3>
                  <p className="text-[#C9D1C5] leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRACTICE / ABOUT */}
      <section id="practice" className="scroll-mt-24 max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-6">
          <div className="relative arch overflow-hidden aspect-[4/5] bg-[#E3DCCF]">
            <img src={CLINIC_IMG} alt="The Palmisano family" className="w-full h-full object-cover object-top" />
          </div>
        </Reveal>
        <div className="lg:col-span-6">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C05C42] mb-4">The Practice</p>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight leading-tight text-[#2A2E26] mb-6">
            A calmer kind of <span className="italic text-[#C05C42]">dentist</span>.
          </h2>
          <p className="text-lg text-[#4A4E46] leading-relaxed mb-6">
            For over two decades, the Palmisano family has treated patients like
            neighbors. Warm rooms, unhurried visits, and a team that remembers your
            name — dentistry that feels calm, personal and genuinely caring.
          </p>
          <div className="grid grid-cols-3 gap-6 mt-10">
            {[["20+", "Years caring"], ["8k+", "Happy smiles"], ["4.9★", "Google rating"]].map(([n, l]) => (
              <div key={l}>
                <p className="font-display text-4xl text-[#C05C42]">{n}</p>
                <p className="text-sm text-[#4A4E46] mt-1">{l}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex items-center gap-3 text-[#4A4E46]">
            <Stethoscope size={20} className="text-[#C05C42]" />
            <span>Accepting new patients this season.</span>
          </div>
        </div>
      </section>
    </main>
  );
}
